import * as React from 'react';
import { ReactElement, useState } from 'react';
import { AttributeOptionTypeEnum } from '../../../common/enum';
import { ImportSettings } from './';
import { toCamelCase, formatBytes } from '../../../common/UtilFunctions';
import { styled } from '@mui/material/styles';
import { 
    Checkbox,
    FormControlLabel,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import ImageIcon from '@mui/icons-material/Image';

export const ImportSheet = (props: ImportSheetProps) => {
    const { name, columns, sheet, sheetMapper, logger } = props;

    const [isIgnoreAll, setIsIgnoreAll] = useState(false);
    const [sheetSettings, setSheetSettings] = useState<any>([]);
    const [imageFiles, setImageFiles] = useState<any>([]);
    const [ignoredAttributes, setIgnoredAttributes] = useState<any>([]);
    const [assignAttributes, setAssignAttributes] = useState<any>([]);
    const availableAttributes: any = [];

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleIgnoreAll = () => {
        setIsIgnoreAll(!isIgnoreAll);

        if (!isIgnoreAll) {
            setIgnoredAttributes([...availableAttributes]);
            setAssignAttributes([]);
        } else {
            setIgnoredAttributes([]);
        }
    }

    const handleIgnore = (e) => {
        const { name, id, checked } = e.target;

        if (checked) {
            setIgnoredAttributes([...ignoredAttributes, name]);
            removeAssign(id);
        } else {
            const newIgnoredAttributes = [...ignoredAttributes];
            var ignoredIndex = newIgnoredAttributes.indexOf(name);
            if (ignoredIndex !== -1) {
                newIgnoredAttributes.splice(ignoredIndex, 1);
                setIgnoredAttributes(newIgnoredAttributes);                
            }
        }
    }

    const handleAssign = (e) => {
        const { id, checked } = e.target;

        if (checked) {
            setAssignAttributes([...assignAttributes, id]);
        } else {
            removeAssign(id);
        }
    }

    const removeAssign = (id) => {
        const newAssignAttributes = [...assignAttributes];
        var assignIndex = newAssignAttributes.indexOf(id);
        if (assignIndex !== -1) {
            newAssignAttributes.splice(assignIndex, 1);
            setAssignAttributes(newAssignAttributes);                
        }
    }

    const handleDialogOpen = (e) => {
        e.stopPropagation();
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
    };

    const handleFileLoad = (e) => {
        e.stopPropagation();

        if (!e.target.files || e.target.files.length === 0) {
          return;
        }

        setImageFiles([...e.target.files]);
    };

    const mapAvailableAttributes = () => {
        return sheetMapper.items.flatMap((item) => {
            if (item.primary_key == 'yes') {
                let code = item.attribute ? item.attribute.code : item.attribute_key;

                return {name: item.name, code: code, primary: item.primary_key};                
            }

            if (ignoredAttributes.includes(item.name)) {
                return [];
            }

            if (availableAttributes.includes(item.name)) {
                let code = item.attribute ? item.attribute.code : item.attribute_key;

                return {name: item.name, code: code, primary: item.primary_key};
            }

            return [];
        });
    };
    
    return (
        <React.Fragment>
            { sheetMapper &&
            <Root>
                <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={toCamelCase(name)+"-content"}
                      id={toCamelCase(name)+"-header"}
                      sx={{ '.MuiAccordionSummary-content': {justifyContent: 'space-between'} }}
                    >
                        <Typography sx={{ lineHeight: '2' }}>{name}</Typography>

                        <div>
                            { columns.includes('Image') &&
                            <Button 
                                component="label"
                                variant="contained" 
                                sx={{ textTransform: "none", marginRight: '20px' }} 
                                size="small"
                            >
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        multiple
                                        hidden 
                                        onChange={handleFileLoad} 
                                    />
                                    <ImageIcon sx={{ paddingRight: '5px' }}/>
                                    Load Images
                                
                            </Button>
                            }

                            <Button 
                                variant="contained" 
                                sx={{ textTransform: "none", marginRight: '20px' }} 
                                size="small"
                                onClick={handleDialogOpen}
                            >
                                <SettingsIcon sx={{ paddingRight: '5px' }}/>Import Setttings
                            </Button>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="left" sx={{ width: '125px'}}>
                                            <FormControlLabel control={<Checkbox onClick={handleIgnoreAll}/>} label="All/None" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sheetMapper.items.map((item) => {
                                        let code = item.attribute ? item.attribute.code : item.attribute_key;

                                        if (columns.includes(item.name) && !availableAttributes.includes(item.name) && item.primary_key == 'no') {
                                            availableAttributes.push(item.name);
                                        }

                                        return (
                                            <React.Fragment key={item.id}>
                                                { columns.includes(item.name) &&
                                                <TableRow>
                                                    <TableCell scope="row">
                                                        <Typography variant="subtitle1">{item.name} ({code})</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        { item.attribute && AttributeOptionTypeEnum.includes(item.attribute.type) &&
                                                            <Tooltip placement="top" title="Add value to attribute options if it doesn't exist.">
                                                                <FormControlLabel 
                                                                    control={
                                                                        <Checkbox 
                                                                            id={code} 
                                                                            onClick={handleAssign} 
                                                                            disabled={ignoredAttributes.includes(item.name)}
                                                                            checked={assignAttributes.includes(code)}
                                                                        />
                                                                    } 
                                                                    label="Assign" 
                                                                />
                                                            </Tooltip>
                                                        }
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        { item.primary_key === 'yes' ?
                                                            <Typography variant="subtitle1">Primary Key</Typography>
                                                        :
                                                            <FormControlLabel 
                                                                control={
                                                                    <Checkbox 
                                                                        name={item.name} 
                                                                        id={code} 
                                                                        onClick={handleIgnore} 
                                                                        checked={ignoredAttributes.includes(item.name)}
                                                                    />
                                                                } 
                                                                label="Ignore" 
                                                            />
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                                }
                                            </React.Fragment>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>         
                    </AccordionDetails>
                </Accordion>

                { imageFiles.length > 0 &&
                <Accordion sx={{ backgroundColor: '#f7f7f7' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="acceptedFiles-content"
                      id="acceptedFiles-header"
                      sx={{ '.MuiAccordionSummary-content': {justifyContent: 'space-between'} }}
                    >
                        <Typography sx={{ lineHeight: '2' }}>Loaded Images</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                {imageFiles.map((item: any, index: number) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell scope="row">
                                                <Typography variant="subtitle1">{item.name}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="subtitle1">{formatBytes(item.size)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>   
                }

                <ImportSettings 
                    name={name} 
                    sheet={sheet}
                    columns={columns}
                    availableAttributes={mapAvailableAttributes()} 
                    assignAttributes={assignAttributes}
                    imageFiles={imageFiles}
                    onClose={handleDialogClose}
                    open={isDialogOpen}
                    logger={logger}
                />
            </Root>
            }
        </React.Fragment>
    )
}

const PREFIX = 'ImportSheetContainer';

const Root = styled('div', {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root 
})(({ theme }) => ({
    color: theme.palette.secondary.main,
    marginTop: '30px',
    marginBottom: '30px',
    paddingRight: '80px'
}));

export interface ImportSheetProps {
    name?: string,
    columns: string[],
    sheet: any[],
    sheetMapper: any,
    logger: (data: string) => void
}